/**
 * 쿼리스트링을 객체로 변환한다.
 * - 같은 키가 여러 번 존재하면 배열로 수집한다.
 * - 기본적으로 현재 `window.location.search`를 사용한다.
 */
export function getQueryParams(
  searchInput?: string
): Record<string, string | string[]> {
  const search =
    typeof searchInput === "string"
      ? normalizeSearch(searchInput)
      : typeof window !== "undefined"
      ? window.location.search
      : "";

  const params = new URLSearchParams(search);
  const result: Record<string, string | string[]> = {};

  // URLSearchParams는 키 순회를 보장하지 않지만, 일반적인 사용에는 문제가 없음
  params.forEach((value, key) => {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const existing = result[key];
      if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        result[key] = [existing, value];
      }
    } else {
      result[key] = value;
    }
  });

  return result;
}

/**
 * 단일 쿼리 파라미터 값을 반환한다.
 * - 값이 여러 개인 경우 첫 번째 값을 반환한다.
 */
export function getQueryParam(
  name: string,
  defaultValue?: string
): string | undefined {
  if (!name) return defaultValue;
  const params = getQueryParams();
  const value = params[name];
  if (Array.isArray(value)) return value[0] ?? defaultValue;
  return value ?? defaultValue;
}

/**
 * `?` 유무와 해시 프래그먼트가 섞인 입력을 정규화한다.
 */
function normalizeSearch(raw: string): string {
  if (!raw) return "";
  let s = raw.trim();
  // 전체 URL이 들어온 경우
  try {
    if (/^https?:\/\//i.test(s)) {
      const u = new URL(s);
      return u.search;
    }
  } catch {
    // 무시: 단순 search 문자열로 취급
  }
  // 해시 앞에 쿼리가 오는 형태 처리: ?a=1#foo -> ?a=1
  const hashIndex = s.indexOf("#");
  if (hashIndex >= 0) {
    s = s.slice(0, hashIndex);
  }
  // "a=1&b=2" 형태면 앞에 '?' 부착
  if (s && s[0] !== "?") {
    s = `?${s}`;
  }
  return s;
}
